import React, { useCallback, useState } from 'react';
import { Button, Checkbox, Input, Modal, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { P5M3Row, useP5M3 } from './hooks/useP5M3';
import { p5m3Api } from '../../services/p5m3Api';
import { BatchAcceptanceBridge, pwp5m3f1AcceptanceManifest } from '../../features/acceptance';
import './P5M3Form.css';

const formatDate = (value: string | null): string => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : value;
};

const P5M3Form: React.FC = () => {
  const navigate = useNavigate();
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const {
    rows, loading, reverseLoading, queried, selectedCount,
    lastApiResponse,
    query, toggle, selectAll, reverse,
  } = useP5M3();

  const runQuery = useCallback(() => query(start, end), [end, query, start]);
  const allSelected = rows.length > 0 && rows.every((row) => row.selected);
  const candidateRecords = rows.map((row) => row.item);
  const selectedKeys = rows.filter((row) => row.selected).map((row) => row.item.fp230_01);
  const queryAcceptanceExact = useCallback((id: string) => p5m3Api.query(id, id), []);

  const confirmReverse = useCallback(() => {
    if (!selectedCount) return;
    Modal.confirm({
      title: '確認退核',
      content: `確定將選取的 ${selectedCount} 筆物化性測試報告退回未核准狀態？相關客訴與測試投影會一併清理。`,
      okText: '確認退核',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => reverse(start, end),
    });
  }, [end, reverse, selectedCount, start]);

  const columns: ColumnsType<P5M3Row> = [
    {
      title: <Checkbox checked={allSelected} onChange={(event) => selectAll(event.target.checked)} />,
      key: 'selected',
      width: 52,
      align: 'center',
      render: (_, row) => (
        <Checkbox
          checked={row.selected}
          onChange={(event) => toggle(row.item.fp230_01, event.target.checked)}
        />
      ),
    },
    { title: <span data-contract-field="fp230_01">報告單號</span>, dataIndex: ['item', 'fp230_01'], width: 150 },
    {
      title: <span data-contract-field="fp230_11">報告日期</span>, dataIndex: ['item', 'fp230_11'], width: 120,
      render: (value: string | null) => formatDate(value),
    },
    { title: <span data-contract-field="fp230_13">送測單號</span>, dataIndex: ['item', 'fp230_13'], width: 150 },
    { title: <span data-contract-field="fp230_14">項次</span>, dataIndex: ['item', 'fp230_14'], width: 80 },
    { title: <span data-contract-field="fp230_16">顏色</span>, dataIndex: ['item', 'fp230_16'], width: 160 },
    { title: <span data-contract-field="fp230_18">廠商</span>, dataIndex: ['item', 'fp230_18'], width: 130 },
    {
      title: <span data-contract-field="fp230_80">狀態</span>, dataIndex: ['item', 'fp230_80'], width: 100, align: 'center',
      render: (value: string) => <Tag color="success">{value === 'P' ? '已核准' : value}</Tag>,
    },
  ];

  return (
    <div className="p5m3-form">
      <div className="p5m3-header">
        <div>
          <h1>物化性測試報告退核 <Tag>P5M3</Tag></h1>
          <p>僅顯示已核准且尚未連結已核准客訴投影的報告。</p>
        </div>
        <Button onClick={() => navigate('/erp')}>返回 ERP</Button>
      </div>

      <div className="p5m3-query">
        <span>報告單號區間</span>
        <Input value={start} onChange={(event) => setStart(event.target.value)} placeholder="起始單號（可空白）" />
        <span>至</span>
        <Input value={end} onChange={(event) => setEnd(event.target.value)} placeholder="結束單號（可空白）" />
        <Button data-acceptance-action="PWP5M3-ACT-QUERY" type="primary" loading={loading} onClick={runQuery}>查詢</Button>
      </div>

      <div className="p5m3-table">
        <div className="p5m3-summary">
          {queried ? `共 ${rows.length} 筆，已選 ${selectedCount} 筆` : '請先查詢可退核報告'}
        </div>
        <Table<P5M3Row>
          rowKey={(row) => row.item.fp230_01}
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={false}
          size="small"
          scroll={{ x: 940, y: 460 }}
          locale={{ emptyText: queried ? '查無可退核資料' : '尚未查詢' }}
        />
      </div>

      <div className="p5m3-actions">
        <Button
          data-acceptance-action="PWP5M3-ACT-REVERSE"
          danger
          type="primary"
          size="large"
          disabled={!selectedCount}
          loading={reverseLoading}
          onClick={confirmReverse}
        >
          退核選取資料（{selectedCount}）
        </Button>
      </div>
      <BatchAcceptanceBridge
        manifest={pwp5m3f1AcceptanceManifest}
        records={candidateRecords}
        selectedKeys={selectedKeys}
        keyOf={(item) => item.fp230_01}
        queryExact={queryAcceptanceExact}
        lastApiResponse={lastApiResponse}
        loading={loading || reverseLoading}
        onAutoFill={() => {
          if (candidateRecords[0]) toggle(candidateRecords[0].fp230_01, true);
        }}
        autoFillDisabled={candidateRecords.length === 0}
        autoFillReason="先查詢 P 狀態且可撤回的報告，再安全勾選第一筆；不會自動撤回。"
        screenDraft={{ start, end, selected_items: selectedKeys, candidates: candidateRecords }}
      />
    </div>
  );
};

export default P5M3Form;
